import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import React from "react";
import styled from "styled-components";

import { useBulkReprocessClaimMutation } from "../../store/queries/claims";
import { ClaimBulkTarget } from "../../types/claim";
import {
  bulkErrorDetail,
  claimCount,
  useBulkActionToast,
} from "./bulkActionShared";

interface IBulkReprocessModalProps {
  visible: boolean;
  onHide: () => void;
  /** The selected rows, or every claim matching the list's filters (CLE-3035). */
  target: ClaimBulkTarget;
  /** How many claims `target` covers — for the copy only. */
  count: number;
  onSuccess: () => void;
}

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 28rem;
`;

const Hint = styled.div`
  color: #6c757d;
  font-size: 0.875rem;
`;

/**
 * CLE-2172 / CLE-3035: confirm modal for bulk reprocess, on any number of
 * claims. The endpoint answers 202 straight away — the reprocess runs in the
 * background on the EDI worker — so the modal closes at once and the progress
 * shows in the banner above the claims list (ClaimBulkActionsBanner), which
 * also announces the finish.
 */
export function BulkReprocessModal({
  visible,
  onHide,
  target,
  count,
  onSuccess,
}: IBulkReprocessModalProps) {
  const { localToast, show } = useBulkActionToast();
  const [bulkReprocess, { isLoading: isStarting }] =
    useBulkReprocessClaimMutation();

  const handleApply = async () => {
    try {
      await bulkReprocess(target).unwrap();
      show({
        severity: "success",
        summary: "Reprocess started",
        detail: `Reprocess started for ${claimCount(
          count
        )}. It runs in the background — progress shows above the list and we'll let you know here when it finishes.`,
        life: 8000,
      });
      onSuccess();
    } catch (err: any) {
      show({
        severity: "error",
        summary: "Could not start the reprocess",
        detail: bulkErrorDetail(err),
        life: 8000,
      });
    }
  };

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button
        label="Cancel"
        className="p-button-text"
        onClick={onHide}
        disabled={isStarting}
      />
      <Button
        label={`Reprocess ${count.toLocaleString("en-US")}`}
        icon="pi pi-refresh"
        onClick={handleApply}
        disabled={isStarting}
        loading={isStarting}
      />
    </div>
  );

  return (
    <>
      <Toast ref={localToast} />
      <Dialog
        header="Reprocess claims"
        visible={visible}
        onHide={isStarting ? () => {} : onHide}
        closable={!isStarting}
        footer={footer}
      >
        <Body>
          <div>
            Reprocess <strong>{claimCount(count)}</strong> from the original
            EDI. The current matched payer is preserved; pricing rules, fee
            schedules, and code mappings are re-evaluated against the latest
            configuration.
          </div>
          <Hint>
            Each claim re-runs its full pricing pipeline from source. This runs
            in the background — you can keep working.
          </Hint>
        </Body>
      </Dialog>
    </>
  );
}
